import { Box, Divider, Grid, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import React from 'react';

import { LeaseExtractionData } from '@/api/endpoints/lease/useLeaseExtract';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { Coins01Icon } from '@/assets/icons/Coins01Icon';
import { Home01Icon } from '@/assets/icons/Home01Icon';
import { Key01Icon } from '@/assets/icons/Key01Icon';
import { ReceiptIcon } from '@/assets/icons/ReceiptIcon';
import { Settings01Icon } from '@/assets/icons/Settings01Icon';
import { User01Icon } from '@/assets/icons/User01Icon';
import { WifiIcon } from '@/assets/icons/WifiIcon';

const formatCurrency = (amount: number | null): string => {
  if (amount === null) return 'Not specified';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'Not specified';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (!Number(date.getTime())) {
      return 'Invalid date';
    }

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  } catch (error) {
    return 'Invalid date';
  }
};

interface LeaseExtractPanelProps {
  data?: LeaseExtractionData;
  isLoading: boolean;
  error?: Error;
}

interface DataSectionProps {
  title: string;
  children: React.ReactNode;
  isLoading: boolean;
  icon: React.ReactNode;
}

const DataSection: React.FC<DataSectionProps> = ({ title, children, isLoading, icon }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: 'primary.light',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box
          sx={{
            mr: 1.5,
            display: 'flex',
            p: 1,
            borderRadius: 1,
            bgcolor: 'primary.lighter',
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {isLoading ? (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="60%" height={24} />
        </Stack>
      ) : (
        children
      )}
    </Paper>
  );
};

interface DataRowProps {
  label: string;
  value: string | number | React.ReactNode;
}

const DataRow: React.FC<DataRowProps> = ({ label, value }) => (
  <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body1" fontWeight="medium" color="text.primary">
      {value}
    </Typography>
  </Box>
);

const LeaseExtractPanel: React.FC<LeaseExtractPanelProps> = ({ data, isLoading, error }) => {
  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          borderRadius: 2,
          border: 1,
          borderColor: 'error.light',
          bgcolor: 'error.lighter',
        }}
      >
        <Typography variant="subtitle1" color="error" fontWeight="medium">
          Error Loading Lease Data
        </Typography>
        <Typography variant="body2" color="error.dark">
          {error.message}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h5" fontWeight="bold" color="primary.main" gutterBottom>
        Lease Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DataSection title="Tenant Information" isLoading={isLoading} icon={<User01Icon />}>
            {data?.tenant && (
              <>
                <DataRow
                  label="Name"
                  value={`${data.tenant.first_name} ${data.tenant.last_name}`}
                />
                <DataRow label="Email" value={data.tenant.email} />
                <DataRow label="Phone" value={data.tenant.phone} />
              </>
            )}
          </DataSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <DataSection title="Property Details" isLoading={isLoading} icon={<Home01Icon />}>
            <DataRow label="Address" value={data?.address || 'Not specified'} />
          </DataSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <DataSection title="Lease Period" isLoading={isLoading} icon={<CalendarIcon />}>
            <DataRow
              label="Start Date"
              value={data?.start_date ? formatDate(data.start_date) : 'Not specified'}
            />
            <DataRow
              label="End Date"
              value={data?.end_date ? formatDate(data.end_date) : 'Not specified'}
            />
            {data?.due_date && (
              <DataRow
                label="Due Date"
                value={data?.due_date ? formatDate(data.due_date) : 'Not specified'}
              />
            )}
          </DataSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <DataSection title="Financial Details" isLoading={isLoading} icon={<Coins01Icon />}>
            <DataRow
              label="Base Rent"
              value={data?.base_rent ? formatCurrency(data.base_rent) : 'Not specified'}
            />
            <DataRow
              label="Total Monthly Amount Due"
              value={
                data?.total_monthly_amount_due
                  ? formatCurrency(data.total_monthly_amount_due)
                  : 'Not specified'
              }
            />
            <DataRow
              label="Security Deposit"
              value={
                data?.security_deposit
                  ? formatCurrency(data.security_deposit.amount)
                  : 'Not specified'
              }
            />
            {data?.security_deposit && (
              <>
                <DataRow label="Deposit Held By" value={data.security_deposit.held_by} />
                <DataRow
                  label="Refund Conditions"
                  value={data.security_deposit.refund_conditions}
                />
              </>
            )}
          </DataSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <DataSection title="Payment Details" isLoading={isLoading} icon={<ReceiptIcon />}>
            <DataRow label="Payment Method" value={data?.payment_method || 'Not specified'} />

            {data?.late_fee && (
              <>
                <DataRow label="Late Fee" value={formatCurrency(data.late_fee.amount)} />
                <DataRow label="Grace Period" value={`${data.late_fee.grace_period_days} days`} />
                {data.late_fee.additional_daily_fee && (
                  <DataRow
                    label="Additional Daily Fee"
                    value={formatCurrency(data.late_fee.additional_daily_fee)}
                  />
                )}
              </>
            )}
          </DataSection>
        </Grid>

        {data?.rent_adjustments && (
          <Grid item xs={12} md={6}>
            <DataSection title="Rent Adjustments" isLoading={isLoading} icon={<Settings01Icon />}>
              {data.rent_adjustments.annual_increase && (
                <DataRow
                  label="Annual Increase"
                  value={`${data.rent_adjustments.annual_increase}%`}
                />
              )}
              {data.rent_adjustments.renewal_options && (
                <DataRow label="Renewal Options" value={data.rent_adjustments.renewal_options} />
              )}
            </DataSection>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <DataSection title="Utilities & Amenities" isLoading={isLoading} icon={<WifiIcon />}>
            {data?.utilities && (
              <>
                <DataRow
                  label="Utilities Included"
                  value={data.utilities.included ? 'Yes' : 'No'}
                />
                <DataRow
                  label="Tenant Responsible for Utilities"
                  value={data.utilities.tenant_responsible ? 'Yes' : 'No'}
                />
              </>
            )}

            {data?.amenities_fee?.monthly_fee && (
              <DataRow
                label="Monthly Amenities Fee"
                value={formatCurrency(data.amenities_fee.monthly_fee)}
              />
            )}

            {data?.parking_fee?.monthly_fee && (
              <DataRow
                label="Monthly Parking Fee"
                value={formatCurrency(data.parking_fee.monthly_fee)}
              />
            )}
          </DataSection>
        </Grid>

        {data?.pet_fee && (data.pet_fee.monthly_fee || data.pet_fee.one_time_fee) && (
          <Grid item xs={12} md={6}>
            <DataSection title="Pet Policy" isLoading={isLoading} icon={<User01Icon />}>
              {data.pet_fee.monthly_fee && (
                <DataRow label="Monthly Pet Fee" value={formatCurrency(data.pet_fee.monthly_fee)} />
              )}
              {data.pet_fee.one_time_fee && (
                <DataRow
                  label="One-time Pet Fee"
                  value={formatCurrency(data.pet_fee.one_time_fee)}
                />
              )}
            </DataSection>
          </Grid>
        )}

        {data?.other_charges &&
          Object.entries(data.other_charges).some(([_, value]) => value !== null) && (
          <Grid item xs={12} md={6}>
            <DataSection title="Other Charges" isLoading={isLoading} icon={<Key01Icon />}>
              {data.other_charges.key_replacement_fee && (
                <DataRow
                  label="Key Replacement Fee"
                  value={formatCurrency(data.other_charges.key_replacement_fee)}
                />
              )}
              {data.other_charges.maintenance_call_fee && (
                <DataRow
                  label="Maintenance Call Fee"
                  value={formatCurrency(data.other_charges.maintenance_call_fee)}
                />
              )}
              {data.other_charges.early_termination_fee && (
                <DataRow
                  label="Early Termination Fee"
                  value={formatCurrency(data.other_charges.early_termination_fee)}
                />
              )}
            </DataSection>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default LeaseExtractPanel;
